import { useLogRequests } from '../api/hooks';
import { ApiError } from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { StateMessage } from '../components/StateMessage';
import { formatDateTime } from '../util/format';

/**
 * Shortens an FCM message id (`projects/<project>/messages/<id>`) to its final
 * segment for compact display; the full value is shown on hover.
 */
function shortMessageId(id: string): string {
  const parts = id.split('/');
  return parts[parts.length - 1] || id;
}

/**
 * Requests page: a live table of all log-fetch requests. Polls every few
 * seconds (via the hook) so operators watch PENDING requests resolve to
 * COMPLETED or EXPIRED in real time.
 */
export function RequestsPage() {
  const { data: requests, isLoading, isError, error } = useLogRequests();

  if (isLoading) {
    return <StateMessage title="Ładowanie żądań…" />;
  }
  if (isError) {
    return (
      <StateMessage tone="error" title="Nie udało się pobrać żądań">
        {(error as ApiError)?.message}
      </StateMessage>
    );
  }
  if (!requests || requests.length === 0) {
    return (
      <StateMessage title="Brak żądań">
        Wyślij żądanie z zakładki „Urządzenia".
      </StateMessage>
    );
  }

  return (
    <section>
      <div className="page-head">
        <h2>Żądania</h2>
        <span className="muted">{requests.length} · odświeżanie na żywo</span>
      </div>

      <div className="card">
        <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Urządzenie</th>
              <th>Miasto</th>
              <th>ID żądania / FCM</th>
              <th>Plików</th>
              <th>Odebrano</th>
              <th>Utworzono</th>
              <th>Uwagi</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.requestId}>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td>
                  <strong>{r.deviceName}</strong>
                  <div className="mono muted small">{r.deviceHash}</div>
                </td>
                <td>{r.cityName}</td>
                <td className="small">
                  <div className="mono" title={`requestId (= nazwa folderu z logami): ${r.requestId}`}>
                    {r.requestId}
                  </div>
                  <div className="mono muted" title={r.fcmMessageId ?? 'brak — nie wysłano'}>
                    FCM: {r.fcmMessageId ? shortMessageId(r.fcmMessageId) : '—'}
                  </div>
                </td>
                <td>{r.filesCount}</td>
                <td>{r.status === 'COMPLETED' ? r.filesReceived : '—'}</td>
                <td className="muted small">{formatDateTime(r.createdAt)}</td>
                <td className="muted small">{r.note ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </section>
  );
}
